/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WASP Frontend v2.5.0 - SchedulerApp (OPTIMIZED)
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useMemo } from 'react';
import { Container, Card, Nav, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faClock,
  faRepeat,
  faHistory,
  faCog,
  faPlus,
  faCheckCircle,
  faExclamationCircle,
  faCalendarDay,
  faBolt
} from '@fortawesome/free-solid-svg-icons';

import { useScheduler } from '../../../hooks/useScheduler';
import { useRecurringCampaigns } from '../../../hooks/useRecurringCampaigns';
import { useTimeZone } from '../../../hooks/useTimeZone';

import CalendarView from './CalendarView';
import ScheduleList from './ScheduleList';
import RecurringCampaigns from './RecurringCampaigns';
import ScheduleHistoryTab from './ScheduleHistoryTab';
import ScheduleEventModal from './ScheduleEventModal';
import SchedulerConfigurationTab from './SchdulerConfigurationTab';
import RecurringTab from './RecurringTab';

import { ScheduledMessage } from '../../../types/scheduler.types';

type TabId = 'calendar' | 'scheduled' | 'recurring' | 'history' | 'settings';

interface Tab {
  id: TabId;
  label: string;
  icon: any;
  badge?: number;
}

interface StatCard {
  value: number;
  label: string;
  icon: any;
  color: string;
  bgColor: string;
}

const SchedulerApp: React.FC = () => {
  const { schedules, stats, isLoading: schedulesLoading } = useScheduler();
  const { activeCampaigns, isLoading: campaignsLoading } = useRecurringCampaigns();
  const { currentTimezone, currentTimezoneInfo } = useTimeZone();

  const [activeTab, setActiveTab] = useState<TabId>('calendar');
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<ScheduledMessage | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Calcular stats reales desde schedules
  const computedStats = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const pending = schedules.filter(s => s.status === 'pending').length;
    const sent = schedules.filter(s => s.status === 'sent').length;
    const failed = schedules.filter(s => s.status === 'failed').length;
    
    const upcomingToday = schedules.filter(s => {
      const scheduleDate = new Date(s.schedule.scheduledAt);
      return s.status === 'pending' && 
             scheduleDate.getFullYear() === today.getFullYear() &&
             scheduleDate.getMonth() === today.getMonth() &&
             scheduleDate.getDate() === today.getDate();
    }).length;

    const campaigns = activeCampaigns?.length || 0;

    return {
      pending,
      sent,
      failed,
      upcomingToday,
      campaigns,
      total: schedules.length
    };
  }, [schedules, activeCampaigns]);

  const statCards: StatCard[] = [
    {
      value: computedStats.pending,
      label: 'Pendientes',
      icon: faClock,
      color: '#f59e0b',
      bgColor: 'rgba(245, 158, 11, 0.1)'
    },
    {
      value: computedStats.sent,
      label: 'Enviados',
      icon: faCheckCircle,
      color: '#10b981',
      bgColor: 'rgba(16, 185, 129, 0.1)'
    },
    {
      value: computedStats.upcomingToday,
      label: 'Hoy',
      icon: faCalendarDay,
      color: '#2ca799',
      bgColor: 'rgba(44, 167, 153, 0.1)'
    },
    {
      value: computedStats.campaigns,
      label: 'Campañas',
      icon: faBolt,
      color: '#3b82f6',
      bgColor: 'rgba(59, 130, 246, 0.1)'
    }
  ];

  const tabs: Tab[] = [
    {
      id: 'calendar',
      label: 'Calendario',
      icon: faCalendar,
      badge: computedStats.upcomingToday
    },
    {
      id: 'scheduled',
      label: 'Programados',
      icon: faClock,
      badge: computedStats.pending
    },
    {
      id: 'recurring',
      label: 'Recurrentes',
      icon: faRepeat,
      badge: computedStats.campaigns
    },
    {
      id: 'history',
      label: 'Historial',
      icon: faHistory
    },
    {
      id: 'settings',
      label: 'Configuración',
      icon: faCog
    }
  ];

  const handleCreateSchedule = (date?: Date) => {
    setSelectedDate(date || null);
    setEditingEvent(null);
    setShowEventModal(true);
  };

  const handleEditSchedule = (schedule: ScheduledMessage) => {
    setEditingEvent(schedule);
    setShowEventModal(true);
  };

  const handleCloseModal = () => {
    setShowEventModal(false);
    setEditingEvent(null);
    setSelectedDate(null);
  };

  const renderHeader = () => (
    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
      <div>
        <h4 className="mb-1" style={{ color: 'var(--bs-body-color)', fontWeight: 600 }}>
          <FontAwesomeIcon icon={faClock} className="me-2" style={{ color: '#2ca799' }} />
          Scheduler
        </h4>
        <p className="mb-0" style={{ fontSize: '0.85rem', color: 'var(--bs-secondary-color)' }}>
          Programa envíos únicos o campañas recurrentes
          {currentTimezoneInfo && (
            <span className="ms-2">
              · <strong>{currentTimezoneInfo.label}</strong>
            </span>
          )}
        </p>
      </div>
      
      <button
        onClick={() => handleCreateSchedule()}
        style={{
          backgroundColor: '#2ca799',
          color: 'white',
          borderRadius: '8px',
          padding: '8px 20px',
          border: 'none',
          cursor: 'pointer',
          fontSize: '0.9rem',
          fontWeight: 600,
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#248a7e';
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(44, 167, 153, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#2ca799';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <FontAwesomeIcon icon={faPlus} className="me-2" />
        Programar Envío
      </button>
    </div>
  );

  const renderStatsCards = () => (
    <div className="mb-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
      {statCards.map((stat, idx) => (
        <div
          key={idx}
          style={{
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid var(--bs-border-color)',
            backgroundColor: stat.bgColor,
            transition: 'all 0.2s',
            cursor: 'default'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                backgroundColor: stat.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '1rem'
              }}
            >
              <FontAwesomeIcon icon={stat.icon} />
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: stat.color, lineHeight: 1 }}>
                {stat.value}
              </div>
            </div>
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--bs-body-color)', fontWeight: 500 }}>
            {stat.label}
          </div>
        </div>
      ))}
    </div>
  );

  const renderTabContent = () => {
    if (schedulesLoading || campaignsLoading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: '#2ca799' }} role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="text-muted mt-3" style={{ fontSize: '0.9rem' }}>Cargando datos...</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'calendar':
        return (
          <CalendarView 
            onEventClick={(event) => {
              const schedule = schedules.find(s => s.id === event.id);
              if (schedule) handleEditSchedule(schedule);
            }}
            onDateClick={(date) => handleCreateSchedule(date)}
          />
        );
        
      case 'scheduled':
        return <ScheduleList onEditSchedule={handleEditSchedule} />;
        
      case 'recurring':
        return (
          <RecurringTab 
            onEditCampaign={() => {}}
            onCreateCampaign={() => handleCreateSchedule()}
          />
        );
        
      case 'history':
        return <ScheduleHistoryTab />;
        
      case 'settings':
        return <SchedulerConfigurationTab />;
        
      default:
        return null;
    }
  };

  return (
    <Container fluid className="py-4" style={{ maxWidth: '1400px' }}>
      {renderHeader()}
      {renderStatsCards()}
      
      <Card
        style={{
          borderRadius: '12px',
          border: '1px solid var(--bs-border-color)',
          backgroundColor: 'var(--bs-body-bg)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
        }}
      >
        <Card.Header
          style={{
            backgroundColor: 'var(--bs-body-bg)',
            borderBottom: '1px solid var(--bs-border-color)',
            borderRadius: '12px 12px 0 0',
            padding: '12px 20px'
          }}
        >
          <Nav variant="tabs" className="border-0" style={{ gap: '4px' }}>
            {tabs.map(tab => (
              <Nav.Item key={tab.id}>
                <Nav.Link
                  active={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    cursor: 'pointer',
                    border: 'none',
                    borderBottom: activeTab === tab.id ? '2px solid #2ca799' : '2px solid transparent',
                    color: activeTab === tab.id ? '#2ca799' : 'var(--bs-secondary-color)',
                    fontWeight: activeTab === tab.id ? '600' : '400',
                    backgroundColor: 'transparent',
                    transition: 'all 0.2s ease',
                    padding: '8px 16px',
                    fontSize: '0.9rem'
                  }}
                >
                  <FontAwesomeIcon icon={tab.icon} className="me-2" />
                  {tab.label}
                  {tab.badge !== undefined && tab.badge > 0 && (
                    <Badge 
                      bg="none"
                      className="ms-2"
                      style={{
                        backgroundColor: activeTab === tab.id ? '#2ca799' : 'var(--bs-secondary-bg)',
                        color: activeTab === tab.id ? 'white' : 'var(--bs-secondary-color)',
                        fontSize: '0.7rem',
                        padding: '2px 6px',
                        borderRadius: '4px'
                      }}
                    >
                      {tab.badge}
                    </Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </Card.Header>

        <Card.Body style={{ minHeight: '500px', padding: '20px' }}>
          {renderTabContent()}
        </Card.Body>
      </Card>

      <ScheduleEventModal
        show={showEventModal}
        onHide={handleCloseModal}
        event={editingEvent}
        preselectedDate={selectedDate}
        onSave={() => {
          console.log('Schedule saved successfully');
        }}
      />
    </Container>
  );
};

export default SchedulerApp;